import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { ApolloError } from '@apollo/client';

const category = 'guest.contactcollector';

type ButtonType = 'edit' | 'save' | 'navigation' | 'share';

/**
 * Guest-side contact collector elements that can be interacted with.
 * Each element represents a specific UI interaction point for telemetry tracking.
 */
type CCGuestElement = 'visitEventWebsite' | 'provideDetails' | 'saveDetails' | 'modifyDetails' | 'looksGood' | 'confirmAddress' | 'confirmAddressBack' | 'submitContactForm';

/** Extra info for confirm address tracking */
type ConfirmAddressExtraInfo = Readonly<{
  addressOption: 'original' | 'suggested';
}>;

/** Extra info for form submission tracking */
type SubmitContactFormExtraInfo = Readonly<{
  url: string;
  eventHandle: string;
  guestFirstName: string;
  guestLastName: string;
  guestEmail: string;
}>;

/**
 * Discriminated union for buttonInteracted params.
 * Each element that requires extraInfo has its own variant with typed payload.
 */
type CCButtonInteractedParams =
  | Readonly<{
      buttonType: ButtonType;
      element: 'confirmAddress';
      extraInfo: ConfirmAddressExtraInfo;
    }>
  | Readonly<{
      buttonType: ButtonType;
      element: 'submitContactForm';
      extraInfo: SubmitContactFormExtraInfo;
    }>
  | Readonly<{
      buttonType: ButtonType;
      element: Exclude<CCGuestElement, 'confirmAddress' | 'submitContactForm'>;
      extraInfo?: undefined;
    }>;

const contactCollectorGuestTelemetry = createTelemetryObject({
  pages: {
    pageViewed: (pageName: string, extraInfo: Record<string, unknown> = {}) => ({
      category: 'contactCollector',
      page: pageName,
      pagePrefix: 'guest',
      extraInfo,
    }),
  },
  actions: {
    trackError: (action: 'Query' | 'Mutation' | 'Other', error: ApolloError | Error | unknown) => ({
      category,
      action,
      actionType: 'error',
      extraInfo: { error },
    }),

    buttonInteracted: ({ buttonType, element, extraInfo }: CCButtonInteractedParams) => ({
      category,
      action: 'ButtonInteracted',
      extraInfo: {
        name: `contactCollectorGuest_${buttonType}ButtonClicked`,
        buttonType,
        element,
        ...extraInfo,
      },
    }),
  },
});

const { TelemetryProvider, useTelemetry } = createTelemetry(contactCollectorGuestTelemetry);
const useContactCollectorGuestTelemetry = useTelemetry;
export { TelemetryProvider, useContactCollectorGuestTelemetry };
